import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FileText, Trash2, UploadCloud } from 'lucide-react'
import toast from 'react-hot-toast'
import type {
  DocumentoAdjunto,
  DocumentoGuardado,
} from '@/features/almacen/lib/documentos'
import {
  EXTENSIONES_ACEPTADAS,
  MAXIMO_POR_HERRAMIENTA,
  TAMANO_MAXIMO_BYTES,
  describirArchivo,
  esExtensionValida,
  esImagen,
  subirDocumento,
} from '@/features/almacen/lib/documentos'

/**
 * Sección de documentos e información adicional de la herramienta (CR-01).
 *
 * Vive en su propio archivo y no dentro de `HerramientaForm`, que ya pasa de
 * las tres mil líneas y está en la lista de deuda técnica del front.
 *
 * La lista se edita en local y se aplica al Guardar, igual que las fotos: el
 * archivo sí sube al momento —para poder mostrar el avance—, pero el vínculo
 * con la herramienta lo hace el guardado.
 */

/** Un renglón de la lista mientras se está editando. */
interface Renglon {
  /** Identificador local, solo para el `key` y para poder quitarlo. */
  clave: string
  fileName: string
  fileSize: number
  mimeType: string
  /** En nulo mientras sube. Con valor, el archivo ya está en el storage. */
  fileUrl: string | null
  progreso: number
  /** Vista previa local de una imagen, para no descargarla de vuelta. */
  previa?: string
  cancelar?: () => void
}

const CLASE_TARJETA =
  'flex items-center gap-3 rounded-xl border px-3 py-3 min-w-0'

const IconoDeArchivo = ({
  mimeType,
  previa,
  fileUrl,
}: {
  mimeType: string
  previa?: string
  fileUrl?: string | null
}) => {
  const src = previa ?? (esImagen(mimeType) ? (fileUrl ?? undefined) : undefined)

  if (src) {
    return (
      <div
        className="rounded-lg overflow-hidden bg-gray-100 flex-shrink-0"
        style={{ width: 48, height: 48 }}
      >
        <img src={src} alt="" className="w-full h-full object-cover" />
      </div>
    )
  }

  return (
    <div
      className="rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ width: 48, height: 48, backgroundColor: '#E0E7F5' }}
    >
      <FileText className="w-5 h-5" style={{ color: '#315FB9' }} />
    </div>
  )
}

// ─── Zona de carga ───────────────────────────────────────────────────────────

const ZonaDeCarga = ({
  onArchivos,
  deshabilitada,
}: {
  onArchivos: (archivos: File[]) => void
  deshabilitada?: boolean
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [encima, setEncima] = useState(false)

  const soltar = (e: React.DragEvent) => {
    e.preventDefault()
    setEncima(false)
    if (deshabilitada) return
    onArchivos(Array.from(e.dataTransfer.files))
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault()
        if (!deshabilitada) setEncima(true)
      }}
      onDragLeave={() => setEncima(false)}
      onDrop={soltar}
      onClick={() => !deshabilitada && inputRef.current?.click()}
      className={`flex items-center gap-4 rounded-xl border-2 border-dashed px-4 py-4 transition-colors ${
        deshabilitada ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
      } ${encima ? 'border-[#477FFA] bg-[#F6F9FB]' : 'border-gray-200'}`}
    >
      <div
        className="rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ width: 44, height: 44, backgroundColor: '#E0E7F5' }}
      >
        <UploadCloud className="w-5 h-5" style={{ color: '#315FB9' }} />
      </div>
      <div className="min-w-0">
        <p className="text-sm text-gray-800">
          Arrastra los archivos o búscalos en tu equipo.
        </p>
        <p className="text-xs text-gray-500 mt-0.5">
          PDF, Word, Excel, JPG o PNG · hasta 10 MB.
        </p>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={EXTENSIONES_ACEPTADAS}
        className="hidden"
        onChange={(e) => {
          const archivos = Array.from(e.target.files ?? [])
          // Se limpia para poder volver a elegir el mismo archivo.
          e.target.value = ''
          if (archivos.length > 0) onArchivos(archivos)
        }}
      />
    </div>
  )
}

// ─── Lista editable ──────────────────────────────────────────────────────────

/**
 * Carga de documentos. Reporta hacia arriba solo los que ya terminaron de
 * subir: mientras uno va a la mitad no tiene URL y no puede guardarse.
 */
export const CargaDeDocumentos = ({
  documentos,
  onChange,
  onSubiendoChange,
}: {
  documentos: DocumentoAdjunto[]
  onChange: (documentos: DocumentoAdjunto[]) => void
  onSubiendoChange?: (subiendo: boolean) => void
}) => {
  const [renglones, setRenglones] = useState<Renglon[]>(() =>
    documentos.map((d) => ({
      clave: d.fileUrl,
      fileName: d.fileName,
      fileSize: d.fileSize,
      mimeType: d.mimeType,
      fileUrl: d.fileUrl,
      progreso: 100,
    })),
  )

  // El padre solo necesita los terminados; los que van a medias no existen
  // todavía para el guardado.
  const avisarAlPadre = useCallback(
    (lista: Renglon[]) => {
      onChange(
        lista
          .filter((r): r is Renglon & { fileUrl: string } => r.fileUrl !== null)
          .map((r) => ({
            fileUrl: r.fileUrl,
            fileName: r.fileName,
            fileSize: r.fileSize,
            mimeType: r.mimeType,
          })),
      )
      onSubiendoChange?.(lista.some((r) => r.fileUrl === null))
    },
    [onChange, onSubiendoChange],
  )

  // Las vistas previas locales son URLs de objeto: si no se liberan, el
  // archivo se queda en memoria hasta que se recargue la página.
  const previasRef = useRef<string[]>([])
  useEffect(
    () => () => {
      previasRef.current.forEach((url) => URL.revokeObjectURL(url))
    },
    [],
  )

  const agregar = (archivos: File[]) => {
    setRenglones((previos) => {
      const espacio = MAXIMO_POR_HERRAMIENTA - previos.length
      if (espacio <= 0) {
        toast.error(
          `Ya tienes el máximo de ${MAXIMO_POR_HERRAMIENTA} documentos en esta herramienta.`,
        )
        return previos
      }

      const validos: File[] = []
      for (const archivo of archivos) {
        if (!esExtensionValida(archivo.name)) {
          toast.error(`"${archivo.name}" no es un formato permitido.`)
          continue
        }
        if (archivo.size > TAMANO_MAXIMO_BYTES) {
          toast.error(`"${archivo.name}" pasa de los 10 MB.`)
          continue
        }
        validos.push(archivo)
      }

      if (validos.length > espacio) {
        toast.error(
          `Solo caben ${espacio} documento${espacio === 1 ? '' : 's'} más; los demás no se agregaron.`,
        )
      }

      const aSubir = validos.slice(0, espacio)
      const nuevos: Renglon[] = aSubir.map((archivo) => {
        const control = new AbortController()
        const clave = `${Date.now()}-${Math.random().toString(36).slice(2)}`
        let previa: string | undefined
        if (archivo.type.startsWith('image/')) {
          previa = URL.createObjectURL(archivo)
          previasRef.current.push(previa)
        }

        subirDocumento(archivo, {
          alAvanzar: (porcentaje) =>
            setRenglones((lista) =>
              lista.map((r) => (r.clave === clave ? { ...r, progreso: porcentaje } : r)),
            ),
          senal: control.signal,
        })
          .then((subido) => {
            setRenglones((lista) => {
              const siguiente = lista.map((r) =>
                r.clave === clave
                  ? { ...r, fileUrl: subido.fileUrl, mimeType: subido.mimeType, progreso: 100 }
                  : r,
              )
              avisarAlPadre(siguiente)
              return siguiente
            })
          })
          .catch((error: unknown) => {
            // Quitarlo a mano durante la subida entra por aquí, y ahí no hay
            // nada que avisar: el renglón ya no está.
            if (error instanceof DOMException && error.name === 'AbortError') return
            setRenglones((lista) => {
              const siguiente = lista.filter((r) => r.clave !== clave)
              avisarAlPadre(siguiente)
              return siguiente
            })
            toast.error(
              error instanceof Error ? error.message : 'No se pudo subir el archivo.',
            )
          })

        return {
          clave,
          fileName: archivo.name,
          fileSize: archivo.size,
          mimeType: archivo.type,
          fileUrl: null,
          progreso: 0,
          previa,
          cancelar: () => control.abort(),
        }
      })

      const siguiente = [...previos, ...nuevos]
      avisarAlPadre(siguiente)
      return siguiente
    })
  }

  const quitar = (clave: string) => {
    setRenglones((previos) => {
      previos.find((r) => r.clave === clave)?.cancelar?.()
      const siguiente = previos.filter((r) => r.clave !== clave)
      avisarAlPadre(siguiente)
      return siguiente
    })
  }

  return (
    <div className="space-y-4">
      <ZonaDeCarga
        onArchivos={agregar}
        deshabilitada={renglones.length >= MAXIMO_POR_HERRAMIENTA}
      />

      {renglones.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {renglones.map((r) => (
            <div key={r.clave} className={CLASE_TARJETA} style={{ borderColor: '#E0E0E0' }}>
              <IconoDeArchivo mimeType={r.mimeType} previa={r.previa} fileUrl={r.fileUrl} />

              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-800 truncate" title={r.fileName}>
                  {r.fileName}
                </p>

                {r.fileUrl ? (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {describirArchivo(r.fileName, r.fileSize)}
                  </p>
                ) : (
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${r.progreso}%`, backgroundColor: '#477FFA' }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 flex-shrink-0">
                      {r.progreso}%
                    </span>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => quitar(r.clave)}
                title="Quitar documento"
                className="flex-shrink-0 p-1 rounded-lg hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" style={{ color: '#EF4444' }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Sección completa del formulario ─────────────────────────────────────────

export const SeccionDocumentos = ({
  documentos,
  onDocumentosChange,
  informacionAdicional,
  onInformacionAdicionalChange,
  onSubiendoChange,
}: {
  documentos: DocumentoAdjunto[]
  onDocumentosChange: (documentos: DocumentoAdjunto[]) => void
  informacionAdicional: string
  onInformacionAdicionalChange: (valor: string) => void
  onSubiendoChange?: (subiendo: boolean) => void
}) => (
  <div className="bg-white rounded-xl border shadow-sm" style={{ borderColor: '#E0E0E0' }}>
    <div className="px-6 py-4 border-b" style={{ borderColor: '#E0E0E0' }}>
      <h2 className="text-base font-semibold text-gray-800">
        Documentos e información adicional
      </h2>
    </div>

    <div className="p-6">
      <CargaDeDocumentos
        documentos={documentos}
        onChange={onDocumentosChange}
        onSubiendoChange={onSubiendoChange}
      />
    </div>

    <div className="px-6 pb-6 pt-5 border-t" style={{ borderColor: '#E0E0E0' }}>
      <label
        htmlFor="informacionAdicional"
        className="block text-sm font-medium text-gray-700 mb-1.5"
      >
        Información adicional o comentarios
      </label>
      <textarea
        id="informacionAdicional"
        name="informacionAdicional"
        rows={4}
        value={informacionAdicional}
        onChange={(e) => onInformacionAdicionalChange(e.target.value)}
        placeholder="Notas de uso, accesorios incluidos, condiciones de garantía, o cualquier detalle relevante para el que reciba."
        className="w-full px-3 py-2.5 border-0 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 transition-colors bg-[#F6F9FB] resize-y"
      />
    </div>
  </div>
)

// ─── Consulta ────────────────────────────────────────────────────────────────

/** Documentos en solo lectura. Cada tarjeta abre el archivo en otra pestaña. */
const Rejilla = ({ documentos }: { documentos: DocumentoGuardado[] }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    {documentos.map((d) => (
      <a
        key={d.id}
        href={d.fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`${CLASE_TARJETA} hover:border-[#477FFA] transition-colors`}
        style={{ borderColor: '#E0E0E0' }}
      >
        <IconoDeArchivo mimeType={d.mimeType} fileUrl={d.fileUrl} />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-gray-800 truncate" title={d.fileName}>
            {d.fileName}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {describirArchivo(d.fileName, d.fileSize)}
          </p>
        </div>
      </a>
    ))}
  </div>
)

export const DocumentosEnConsulta = ({
  documentos,
  informacionAdicional,
  adjuntosDeMovimientos,
}: {
  documentos: DocumentoGuardado[]
  informacionAdicional: string | null
  /** Agrupados por movimiento; hoy solo la baja adjunta documentos. */
  adjuntosDeMovimientos?: { etiqueta: string; documentos: DocumentoGuardado[] }[]
}) => (
  <div
    className="bg-white rounded-xl border shadow-sm mb-6"
    style={{ borderColor: '#E0E0E0' }}
  >
    <div className="px-6 py-4 border-b" style={{ borderColor: '#E0E0E0' }}>
      <h2 className="text-base font-semibold text-gray-800">
        Documentos e información adicional
      </h2>
    </div>

    <div className="p-6">
      {documentos.length === 0 ? (
        <p className="text-sm text-gray-500">Sin documentos adjuntos</p>
      ) : (
        <Rejilla documentos={documentos} />
      )}

      {/* Los que respaldan un movimiento —hoy solo la baja— van aparte: no son
          papeles de la herramienta, son evidencia de lo que pasó ese día. */}
      {adjuntosDeMovimientos
        ?.filter((g) => g.documentos.length > 0)
        .map((g) => (
          <div key={g.etiqueta} className="mt-6">
            <p className="text-sm font-medium text-gray-700 mb-2.5">{g.etiqueta}</p>
            <Rejilla documentos={g.documentos} />
          </div>
        ))}

      <div className="mt-6">
        <p className="text-sm font-medium text-gray-700 mb-1.5">
          Información adicional o comentarios
        </p>
        <p className="text-sm text-gray-800 break-words" style={{ lineHeight: '1.4' }}>
          {informacionAdicional?.trim() || 'Sin información adicional'}
        </p>
      </div>
    </div>
  </div>
)
